import React, { useEffect, useRef, useState } from "react"
import { useBlocker } from "react-router-dom"
import { Alert, Button, Modal } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import DummyBadge from "features/DummyData/components/DummyBadge.component"
import DummyDataPrompt from "features/DummyData/components/DummyDataPrompt.component"
import { useCatalogueSources } from "../api/useCatalogueSources"
import { usePreparationSources } from "../api/usePreparationSources"
import { buildCatalogue, buildDataOnlyCatalogue } from "../model/catalogue"
import {
  selectIsDirty,
  useTabSpecStore,
  useTabSpecStoreApi,
} from "../runtime/tabSpecStore"
import { useTabSpecQueue } from "../runtime/useTabSpecQueue"
import { useTabSpecSession } from "../runtime/useTabSpecSession"
import { useValueCheck } from "../runtime/useValueCheck"
import AddTableDialog from "./AddTable/AddTableDialog.component"
import BuildFromDataDialog from "./AddTable/BuildFromDataDialog.component"
import BannerDrawer from "./Banners/BannerDrawer.component"
import DataDrawer from "./Data/DataDrawer.component"
import DefaultsDrawer from "./Defaults/DefaultsDrawer.component"
import FindingsStrip, { EmptySpec } from "./Findings/FindingsStrip.component"
import TableGrid from "./Grid/TableGrid.component"
import SpecHeader, { SPEC_DIALOG_Z_INDEX } from "./Header/SpecHeader.component"
import Inspector from "./Inspector/Inspector.component"
import LabelsDrawer from "./Labels/LabelsDrawer.component"
import DownloadDialog from "./Run/DownloadDialog.component"
import RunDialog from "./Run/RunDialog.component"

/** What the tables say about their base while the routing is on its way. */
const ROUTING_PENDING = "Checking who was asked each question..."

const NO_DATA =
  "This project has no data yet. You can set up the tables now and run them after the data is imported."
/** Without a questionnaire nothing can be set up before the data is in. */
const NO_DATA_WITHOUT_QUESTIONNAIRE =
  "This project has no data yet. Its tables are built from the data once it is imported."
const NO_QUESTIONNAIRE =
  "The questionnaire could not be read, so the tables cannot be listed."

/** The drawer or dialog open over the screen; the inspector follows the selected table instead. */
type Panel =
  | "add-table"
  | "build-from-data"
  | "defaults"
  | "data"
  | "labels"
  | "banners"
  | "run"
  | "download"
  | null

/**
 * Asks before unsaved changes are left behind: a dialog for the app's own
 * links (the dashboards' words and buttons, over the spec's drawers), the
 * browser's prompt for a reload or a closed tab. A new search on the same
 * page passes. Opening the stored version ("Reload their version") mounts
 * the spec again and is not a navigation.
 */
const LeaveGuard: React.FC<{ getContainer: () => HTMLElement }> = ({
  getContainer,
}) => {
  const store = useTabSpecStoreApi()
  const dirty = useTabSpecStore(selectIsDirty)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      selectIsDirty(store.getState()) &&
      currentLocation.pathname !== nextLocation.pathname
  )

  useEffect(() => {
    if (!dirty) return
    const onUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", onUnload)
    return () => window.removeEventListener("beforeunload", onUnload)
  }, [dirty])

  return (
    <Modal
      open={blocker.state === "blocked"}
      title="Leave without saving?"
      onCancel={() => blocker.reset?.()}
      getContainer={getContainer}
      zIndex={SPEC_DIALOG_Z_INDEX}
      footer={[
        <DataQButton key="cancel" type="secondary" onClick={() => blocker.reset?.()}>
          Cancel
        </DataQButton>,
        <DataQButton key="leave" type="danger" onClick={() => blocker.proceed?.()}>
          Leave
        </DataQButton>,
      ]}
    >
      <p>Your changes to this tab spec have not been saved.</p>
    </Modal>
  )
}

/**
 * The screen of one tab spec. Wires what the store needs from outside: the
 * question catalogue (questionnaire, codebook and routing), the engine
 * session, its query queue and the values check of the numbers shown value
 * by value. Shows the header, the state of the session,
 * what to review, and the grid of tables. The inspector opens on the
 * selected table; the banners, defaults, data and labels drawers and the add
 * table, run and download dialogs open from the header, one at a time. `onReload` opens
 * the stored version in place of the draft (the container passes it).
 *
 * In a project without a questionnaire (the store's `dataOnly`) neither the
 * questionnaire nor the routing is asked for: the catalogue holds the
 * variables of the data alone, from the moment the screen opens (none until
 * the codebook is in), and "Build tables from the data" makes the tables.
 */
const TabSpecWorkspace: React.FC<{
  onReload?: () => void | Promise<void>
  /** Extra actions for the right end of the header (the assistant's toggle). */
  headerActions?: React.ReactNode
}> = ({ onReload, headerActions }) => {
  const projectId = useTabSpecStore((s) => s.projectId)
  const owner = useTabSpecStore((s) => s.owner)
  const dataOnly = useTabSpecStore((s) => s.dataOnly)
  const variant = useTabSpecStore((s) => s.document.variant)
  const codebook = useTabSpecStore((s) => s.codebook)
  const setCatalogue = useTabSpecStore((s) => s.setCatalogue)
  const hasTables = useTabSpecStore((s) => s.document.tables.length > 0)
  const status = useTabSpecStore((s) => s.session.status)
  const sessionError = useTabSpecStore((s) => s.session.error)
  const prepError = useTabSpecStore((s) => s.session.prepError)
  const routingMessage = useTabSpecStore((s) => s.routingMessage)
  const [panel, setPanel] = useState<Panel>(null)
  /** The variables "Build tables from the data" lists: a finding's, or null for every one. */
  const [buildOnly, setBuildOnly] = useState<string[] | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  // Drawers render inside the spec, so they get the --qc-* tokens.
  const panelContainer = (): HTMLElement =>
    rootRef.current?.closest<HTMLElement>(".tabspec-root") ?? document.body
  const buildFromData = (only: string[] | null): void => {
    setBuildOnly(only)
    setPanel("build-from-data")
  }

  const sources = useCatalogueSources(projectId, owner, variant, !dataOnly)
  // The saved pipelines and scripts: their names for the Data drawer, and
  // the chosen ones read afresh by the session at every preparation.
  const preparation = usePreparationSources(projectId, owner)
  const { restart } = useTabSpecSession(preparation.read)

  // The catalogue follows the questionnaire, the version, the codebook and
  // the routing; the routing arrives last and never holds the screen.
  // Without a questionnaire it follows the codebook alone.
  const { parse, routing, routingLoading } = sources
  useEffect(() => {
    if (dataOnly) {
      setCatalogue(buildDataOnlyCatalogue(codebook), null)
      return
    }
    if (!parse) return
    const catalogue = buildCatalogue({
      parse,
      variant,
      codebook,
      routing: routing?.ok ? routing.info : null,
    })
    const message = routingLoading
      ? ROUTING_PENDING
      : routing && !routing.ok
        ? routing.message
        : null
    // The catalogue carries the loading state, so the tab book waits for the
    // routing and the tables say it is on its way, not that it is missing.
    setCatalogue(routingLoading ? { ...catalogue, routingLoading: true } : catalogue, message)
  }, [dataOnly, parse, variant, codebook, routing, routingLoading, setCatalogue])

  // One queue per session: the inspector's preview and the tab book run
  // their tables through it, and the values check its request.
  const queue = useTabSpecQueue()
  useValueCheck(queue)

  return (
    <div ref={rootRef} className="ts-workspace">
      <SpecHeader
        onOpenDefaults={() => setPanel("defaults")}
        onOpenData={() => setPanel("data")}
        onOpenLabels={() => setPanel("labels")}
        onReload={onReload}
        onAddTable={() => setPanel("add-table")}
        onOpenBanners={() => setPanel("banners")}
        onRunBook={() => setPanel("run")}
        onDownload={() => setPanel("download")}
        badge={<DummyBadge projectId={dataOnly ? undefined : projectId} />}
        actions={headerActions}
      />
      <div className="ts-messages">
        {/* With a questionnaire, dummy data can stand in for the data until it
            is imported; once generated, the session starts again on it. */}
        {status === "no-data" && (
          <Alert
            type="info"
            showIcon
            message={dataOnly ? NO_DATA_WITHOUT_QUESTIONNAIRE : NO_DATA}
            action={
              !dataOnly && projectId ? (
                <DummyDataPrompt
                  projectId={projectId}
                  owner={owner}
                  onGenerated={() => void restart()}
                />
              ) : undefined
            }
          />
        )}
        {status === "error" && (
          <Alert
            type="error"
            showIcon
            message={sessionError ?? "The data session could not be started."}
            action={
              <Button size="small" onClick={() => void restart()}>
                Restart session
              </Button>
            }
          />
        )}
        {status === "prep-error" && (
          <Alert
            type="error"
            showIcon
            message={
              prepError ? `Preparation failed: ${prepError}` : "The data could not be prepared."
            }
            action={
              <Button size="small" onClick={() => setPanel("data")}>
                Open data settings
              </Button>
            }
          />
        )}
        {routingMessage && codebook !== null && (
          <Alert type="info" showIcon message={routingMessage} />
        )}
        {/* Never in a project without a questionnaire: that is its mode, not an error. */}
        {!dataOnly && sources.error && <Alert type="error" showIcon message={NO_QUESTIONNAIRE} />}
      </div>
      <FindingsStrip onChooseTables={buildFromData} />
      <div className="ts-main">
        {!dataOnly && sources.isLoading ? (
          <div className="ts-loading">
            <Loader fontSize={32} />
          </div>
        ) : hasTables ? (
          <TableGrid />
        ) : (
          <EmptySpec onBuildFromData={() => buildFromData(null)} />
        )}
        {/* The dashboards' quiet note over the canvas while the session starts. */}
        {status === "starting" && (
          <div className="ts-session-note" role="status">
            <Loader fontSize={12} color="var(--qc-accent)" />
            Preparing the data
          </div>
        )}
      </div>
      <LeaveGuard getContainer={panelContainer} />
      <Inspector queue={queue} onEditBanners={() => setPanel("banners")} />
      {/* Add selects the first new table, which opens the inspector on it. */}
      <AddTableDialog
        open={panel === "add-table"}
        onClose={() => setPanel(null)}
        onAddSeveral={() => buildFromData(null)}
        getContainer={panelContainer}
      />
      {/* Adds at the end and selects nothing, so the inspector stays closed. */}
      <BuildFromDataDialog
        open={panel === "build-from-data"}
        only={buildOnly}
        onClose={() => setPanel(null)}
        getContainer={panelContainer}
      />
      {/* Shows over the inspector when opened from it: tabSpec.css puts the inspector one layer down. */}
      <BannerDrawer
        open={panel === "banners"}
        onClose={() => setPanel(null)}
        getContainer={panelContainer}
      />
      {/* "Show table" has selected the table, which opens the inspector. */}
      <RunDialog
        open={panel === "run"}
        onClose={() => setPanel(null)}
        queue={queue}
        onShowTable={() => setPanel(null)}
        onDownload={() => setPanel("download")}
      />
      <DownloadDialog
        open={panel === "download"}
        onClose={() => setPanel(null)}
        onRunFirst={() => setPanel("run")}
      />
      <DefaultsDrawer
        open={panel === "defaults"}
        onClose={() => setPanel(null)}
        getContainer={panelContainer}
      />
      <DataDrawer
        open={panel === "data"}
        onClose={() => setPanel(null)}
        variants={sources.variants}
        sources={preparation.names}
        onRestart={() => void restart()}
        getContainer={panelContainer}
      />
      {/* The names the tables show; its body is built only while it is open. */}
      <LabelsDrawer
        open={panel === "labels"}
        onClose={() => setPanel(null)}
        getContainer={panelContainer}
      />
    </div>
  )
}

export default TabSpecWorkspace
